import React, { useState, useEffect, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import axios from 'axios';
import './App.css';

// Critical components loaded immediately (above the fold)
import Header from './components/Header';
import QuickNavBar from './components/QuickNavBar';
import Footer from './components/Footer';
import LoginForm from './components/LoginForm';
import RegisterForm from './components/RegisterForm';
import PublicLandingPage from './components/PublicLandingPage';
import ComponentLoader from './components/LazyComponentLoader';

// Lazy load heavy/non-critical components for better performance
const LandingPage = lazy(() => import('./components/LandingPage'));
const Evolve90Program = lazy(() => import('./components/Evolve90Program'));
const DayReset21 = lazy(() => import('./components/DayReset21'));
const NewsFeed = lazy(() => import('./components/NewsFeed'));
const Groups = lazy(() => import('./components/Groups'));
const Profile = lazy(() => import('./components/Profile'));
const AdminDashboard = lazy(() => import('./components/AdminDashboard'));
const Membership = lazy(() => import('./components/Membership'));
const Social = lazy(() => import('./components/Social'));
const LMS = lazy(() => import('./components/LMS'));
const Library = lazy(() => import('./components/Library'));
const News = lazy(() => import('./components/News'));
const Forum = lazy(() => import('./components/Forum'));
const Journal = lazy(() => import('./components/Journal'));
const HabitTracker = lazy(() => import('./components/HabitTracker'));
const GoalTracker = lazy(() => import('./components/GoalTracker'));
const TermsOfService = lazy(() => import('./components/TermsOfService'));
const PrivacyPolicy = lazy(() => import('./components/PrivacyPolicy'));
const CommunityGuidelines = lazy(() => import('./components/CommunityGuidelines'));
const ContactUs = lazy(() => import('./components/ContactUs'));
const PaymentSuccess = lazy(() => import('./components/PaymentSuccess'));
const RegistrationSuccess = lazy(() => import('./components/RegistrationSuccess'));
const PublicMembershipPage = lazy(() => import('./components/PublicMembershipPage'));
const PublicNews = lazy(() => import('./components/PublicNews'));
const Support = lazy(() => import('./components/Support'));
const VSLSalesPage = lazy(() => import('./components/VSLSalesPage'));
const AffiliatePage = lazy(() => import('./components/AffiliatePage'));
const ThankYou = lazy(() => import('./components/ThankYou'));
const StandWorkshopBooking = lazy(() => import('./components/StandWorkshopBooking'));
const StandPage = lazy(() => import('./components/StandPage'));
import ResetPassword from './components/ResetPassword';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = React.createContext();

export const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchCurrentUser();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchCurrentUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      localStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      const { access_token, user: userData } = response.data;
      
      localStorage.setItem('token', access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      setUser(userData);
      
      // Set default page - evolve90 for all users
      setCurrentPage('evolve90');
      
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Login failed' 
      };
    }
  };

  const register = async (email, password, name, bio, membership_tier = 'starter') => {
    try {
      const response = await axios.post(`${API}/auth/register`, { 
        email, 
        password, 
        name, 
        bio,
        membership_tier 
      });
      const { access_token, user: userData } = response.data;
      
      localStorage.setItem('token', access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      setUser(userData);
      
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Registration failed' 
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
  };

  const value = {
    user,
    login,
    register,
    logout,
    loading,
    fetchCurrentUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Main App Component
const App = () => {
  const [currentPage, setCurrentPage] = useState('feed');
  
  // Handle URL parameters to set initial page
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const pageParam = urlParams.get('page');
    if (pageParam) {
      setCurrentPage(pageParam);
    }
  }, []);

  return (
    <div className="App">
      <AuthProvider>
        <Router>
          <Routes>
            <Route path="/auth/*" element={<AuthPages />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/landing" element={<PublicPageWithFooter><PublicLandingPage /></PublicPageWithFooter>} />
            <Route path="/membership" element={<PublicPageWithFooter><ComponentLoader><PublicMembershipPage /></ComponentLoader></PublicPageWithFooter>} />
            <Route path="/privacy-policy" element={<PublicPageWithFooter><ComponentLoader><PrivacyPolicy /></ComponentLoader></PublicPageWithFooter>} />
            <Route path="/terms-of-service" element={<PublicPageWithFooter><ComponentLoader><TermsOfService /></ComponentLoader></PublicPageWithFooter>} />
            <Route path="/support" element={<PublicPageWithFooter><ComponentLoader><Support /></ComponentLoader></PublicPageWithFooter>} />
            <Route path="/vsl" element={<PublicPageWithFooter><ComponentLoader><VSLSalesPage /></ComponentLoader></PublicPageWithFooter>} />
            <Route path="/affiliate" element={<PublicPageWithFooter><ComponentLoader><AffiliatePage /></ComponentLoader></PublicPageWithFooter>} />
            <Route path="/community-guidelines" element={<PublicPageWithFooter><ComponentLoader><CommunityGuidelines /></ComponentLoader></PublicPageWithFooter>} />
            <Route path="/contact-us" element={<PublicPageWithFooter><ComponentLoader><ContactUs /></ComponentLoader></PublicPageWithFooter>} />
            <Route path="/stand-workshop-booking" element={<PublicPageWithFooter><ComponentLoader><StandWorkshopBooking /></ComponentLoader></PublicPageWithFooter>} />
            <Route path="/stand" element={<ComponentLoader><StandPage /></ComponentLoader>} />
            <Route path="/thankyou" element={<ComponentLoader><ThankYou /></ComponentLoader>} />
            <Route path="/news" element={<ComponentLoader><PublicNews /></ComponentLoader>} />
            <Route path="/news/:postId" element={<ComponentLoader><PublicNews /></ComponentLoader>} />
            <Route path="/*" element={<MainApp currentPage={currentPage} setCurrentPage={setCurrentPage} />} />
          </Routes>
        </Router>
      </AuthProvider>
    </div>
  );
};

const PublicPageWithFooter = ({ children }) => {
  const navigate = useNavigate();
  
  const handlePublicFooterLink = (page) => {
    const publicRoutes = {
      'terms': '/terms-of-service',
      'privacy': '/privacy-policy', 
      'guidelines': '/community-guidelines',
      'support': '/support',
      'contact': '/contact-us'
    };

    if (publicRoutes[page]) {
      navigate(publicRoutes[page]);
    } else {
      // For other pages, navigate to login
      navigate('/auth/login');
    }
  };

  return (
    <div>
      {children}
      <Footer setCurrentPage={handlePublicFooterLink} />
    </div>
  );
};

const AuthPages = () => {
  const { user } = useAuth();
  
  if (user) {
    // Redirect to default page for all users
    return <Navigate to={`/?page=evolve90`} replace />;
  }

  return (
    <div className="auth-container">
      <Routes>
        <Route path="/login" element={<LoginForm />} />
        <Route path="/register" element={<RegisterForm />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/home" element={<PublicLandingPage />} />
        <Route path="/membership" element={<ComponentLoader><PublicMembershipPage /></ComponentLoader>} />
        <Route path="/*" element={<Navigate to="/auth/login" replace />} />
      </Routes>
    </div>
  );
};

const MainApp = ({ currentPage, setCurrentPage }) => {
  const { user, loading } = useAuth();
  
  // Check if we're on the root path
  const isRootPath = window.location.pathname === '/';

  if (loading) {
    return (
      <div className="loading-container">
        <div className="phoenix-logo">
          <img 
            src="/logo.webp" 
            alt="Zero Give Up" 
            className="logo-image"
          />
        </div>
      </div>
    );
  }

  // If no user and on root path, redirect to login
  if (!user && isRootPath) {
    return <Navigate to="/auth/login" replace />;
  }
  
  // If no user but not on root (shouldn't happen with new routing), still redirect to login
  if (!user) {
    return <Navigate to="/auth/login" replace />;
  }

  const renderContent = () => {
    switch (currentPage) {
      case 'landing':
        return (
          <ComponentLoader>
            <LandingPage setCurrentPage={setCurrentPage} />
          </ComponentLoader>
        );
      case 'feed':
        return (
          <ComponentLoader>
            <NewsFeed />
          </ComponentLoader>
        );
      case 'groups':
        return (
          <ComponentLoader>
            <Groups />
          </ComponentLoader>
        );
      case 'forum':
        return (
          <ComponentLoader>
            <Forum />
          </ComponentLoader>
        );
      case 'journal':
        return (
          <ComponentLoader>
            <Journal setCurrentPage={setCurrentPage} />
          </ComponentLoader>
        );
      case 'habit-tracker':
        return (
          <ComponentLoader>
            <HabitTracker />
          </ComponentLoader>
        );
      case 'goal-tracker':
        return (
          <ComponentLoader>
            <GoalTracker />
          </ComponentLoader>
        );
      case 'evolve90':
        return (
          <ComponentLoader>
            <Evolve90Program setCurrentPage={setCurrentPage} user={user} />
          </ComponentLoader>
        );
      case '21-day-reset':
        return (
          <ComponentLoader>
            <DayReset21 setCurrentPage={setCurrentPage} user={user} />
          </ComponentLoader>
        );
      case 'social':
        return (
          <ComponentLoader>
            <Social setCurrentPage={setCurrentPage} />
          </ComponentLoader>
        );
      case 'lms':
        return (
          <ComponentLoader>
            <LMS />
          </ComponentLoader>
        );
      case 'library':
        return (
          <ComponentLoader>
            <Library />
          </ComponentLoader>
        );
      case 'news':
        return (
          <ComponentLoader>
            <News />
          </ComponentLoader>
        );
      case 'profile':
        return (
          <ComponentLoader>
            <Profile />
          </ComponentLoader>
        );
      case 'membership':
        return (
          <ComponentLoader>
            <Membership />
          </ComponentLoader>
        );
      case 'admin':
        return user.is_admin ? (
          <ComponentLoader>
            <AdminDashboard />
          </ComponentLoader>
        ) : (
          <ComponentLoader>
            <NewsFeed />
          </ComponentLoader>
        );
      case 'terms':
        return (
          <ComponentLoader>
            <TermsOfService />
          </ComponentLoader>
        );
      case 'privacy':
        return (
          <ComponentLoader>
            <PrivacyPolicy />
          </ComponentLoader>
        );
      case 'guidelines':
        return (
          <ComponentLoader>
            <CommunityGuidelines />
          </ComponentLoader>
        );
      case 'contact':
        return (
          <ComponentLoader>
            <ContactUs />
          </ComponentLoader>
        );
      case 'payment-success':
        return (
          <ComponentLoader>
            <PaymentSuccess />
          </ComponentLoader>
        );
      case 'registration-success':
        return (
          <ComponentLoader>
            <RegistrationSuccess />
          </ComponentLoader>
        );
      default:
        return (
          <ComponentLoader>
            <NewsFeed />
          </ComponentLoader>
        );
    }
  };

  return (
    <div className="main-app">
      <Header currentPage={currentPage} setCurrentPage={setCurrentPage} />
      <QuickNavBar currentPage={currentPage} setCurrentPage={setCurrentPage} />
      <main className="app-content">
        {renderContent()}
      </main>
      <Footer setCurrentPage={setCurrentPage} />
    </div>
  );
};

export default App;